// Header row of the script boxes in TTSView / STSView / FreeChoiceView:
// a label on the left, COPY (+ optional RESTORE) on the right.
//
// The three call sites render the same controls but live in different CSS
// modules (and FreeChoiceView uses a <label htmlFor> for its textarea), so
// the caller hands in its own class names and label element — the rendered
// DOM and styling stay exactly what each view had before the extraction.

import type { ReactNode } from 'react';
import { CopyButton } from '@/components/ui/CopyButton';

export interface ScriptHeaderClasses {
  /** Row wrapper (label left, actions right). */
  row: string;
  /** Wrapper around the buttons. */
  actions: string;
  /** Pill button (COPY + RESTORE). */
  button: string;
  /** Extra class on COPY while the "COPIED" feedback shows. */
  copied: string;
}

interface ScriptHeaderProps {
  /** Left-hand label, e.g. `<span>Original Script</span>`. */
  label: ReactNode;
  copyText: string;
  classes: ScriptHeaderClasses;
  /** When set, a RESTORE button is rendered after COPY. */
  onRestore?: () => void;
  /** Disables COPY and RESTORE (FreeChoiceView: empty custom script). */
  disabled?: boolean;
}

export function ScriptHeader({ label, copyText, classes, onRestore, disabled }: ScriptHeaderProps) {
  return (
    <div className={classes.row}>
      {label}
      <div className={classes.actions}>
        <CopyButton
          text={copyText}
          className={classes.button}
          copiedClassName={classes.copied}
          disabled={disabled}
        />
        {onRestore && (
          <button
            type="button"
            className={classes.button}
            onClick={onRestore}
            disabled={disabled}
            tabIndex={-1}
          >
            RESTORE
          </button>
        )}
      </div>
    </div>
  );
}
